import { z } from 'zod';

import type { PortfolioParams } from '@/repository/agency';
import { PortfolioFilter, PortfolioSortField } from '@/types/generated/enums';

// The API refuses a longer search, which would fail the whole page.
export const SEARCH_MAX_LENGTH = 100;

const absent = (): undefined => {};

// A value it does not know reads as absent, so a mangled link still opens the page.
const lenient = <T extends z.ZodType>(schema: T) =>
  schema.optional().catch(absent);

export const portfolioSearchSchema = z.object({
  status: lenient(z.enum(PortfolioFilter)),
  mine: lenient(z.boolean()),
  q: lenient(z.string().trim().max(SEARCH_MAX_LENGTH)),
  sort: lenient(z.enum(PortfolioSortField)),
  dir: lenient(z.enum(['asc', 'desc'])),
  offset: lenient(z.number().int().nonnegative()),
});

export type PortfolioSearch = z.infer<typeof portfolioSearchSchema>;

export const PORTFOLIO_PAGE_SIZE = 20;

export const DEFAULT_SORT: PortfolioSortField = 'SPEND';

// The query a view of the page asks; the tour gate asks the unfiltered one to share its cache entry.
export const portfolioParams = (search: PortfolioSearch): PortfolioParams => {
  const sort = search.sort ?? DEFAULT_SORT;
  return {
    status: search.status,
    mine: search.mine,
    search: search.q,
    sort_by: sort,
    sort_dir: search.dir ?? (sort === 'NAME' ? 'asc' : 'desc'),
    limit: PORTFOLIO_PAGE_SIZE,
    offset: search.offset ?? 0,
  };
};
